import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider } from './hooks/useAuth.jsx';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import TemplatesPage from './pages/TemplatesPage';
import HelpPage from './pages/HelpPage';
import AgreementWizard from './pages/AgreementWizard';
import CollaborationRoom from './pages/CollaborationRoom';
import DocumentPreview from './pages/DocumentPreview';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import PWAPrompt from './components/PWAPrompt';
import AnalyticsTracker from './components/AnalyticsTracker';
import './App.css';

function App() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);

  const handleAuthRequired = (mode = 'signin') => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  // Check for PWA prompt after user interaction
  React.useEffect(() => {
    const handleUserInteraction = () => {
      // Show PWA prompt after some interactions
      setTimeout(() => {
        if ('serviceWorker' in navigator && window.matchMedia('(display-mode: standalone)').matches === false) {
          setShowPWAPrompt(true);
        }
      }, 30000); // Show after 30 seconds of activity
    };

    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);

    return () => {
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AnalyticsTracker />
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <Navbar onAuthRequired={handleAuthRequired} />
            <motion.main
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="min-h-screen"
            >
              <Routes>
                <Route path="/" element={<HomePage onAuthRequired={handleAuthRequired} />} />
                <Route path="/templates" element={<TemplatesPage onAuthRequired={handleAuthRequired} />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/wizard/:type" element={<AgreementWizard onAuthRequired={handleAuthRequired} />} />
                <Route path="/collaborate/:sessionId" element={<CollaborationRoom />} />
                <Route path="/preview/:sessionId" element={<DocumentPreview />} />
              </Routes>
            </motion.main>
            <Footer />
            <AuthModal
              isOpen={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
              initialMode={authMode}
            />
            <PWAPrompt
              show={showPWAPrompt}
              onClose={() => setShowPWAPrompt(false)}
            />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;